import {
  ApolloClient,
  NormalizedCacheObject,
  InMemoryCache,
  createHttpLink,
  Observable,
  Operation,
  FetchResult,
} from "@apollo/client";

import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { getAuth } from "firebase/auth";
import localStorageKeys from "../../constants/local-storage-keys";

import { environment } from "../environment/environment";

const GQL_ENDPOINT = environment.gqlEndpoint;

/**
 * Waits for Firebase authentication to initialize
 * @returns Promise<void>
 */
const waitForAuthInit = async (): Promise<void> => {
  const auth = getAuth();
  if (auth.currentUser) return;

  return new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve();
    });
  });
};

/**
 * Gets a fresh Firebase token with configurable options
 * @param options - Configuration object for token retrieval
 * @returns Promise<string | null> A promise that resolves to the new token or null if unable to get one
 */
type GetTokenOptions = {
  forceRefresh?: boolean; // Whether to force a new token from Firebase
  maxRetries?: number; // Maximum number of retry attempts
  retryDelay?: number; // Delay between retries in milliseconds
};

const getFreshToken = async ({
  forceRefresh = false,
  maxRetries = 3,
  retryDelay = 1000,
}: GetTokenOptions = {}): Promise<string | null> => {
  const auth = getAuth();

  // Helper function for retry logic
  const attemptGetToken = async (
    retriesLeft: number
  ): Promise<string | null> => {
    try {
      // Wait for auth initialization on first try
      if (retriesLeft === maxRetries) {
        await waitForAuthInit();
      }

      const user = auth.currentUser;
      if (!user) {
        if (retriesLeft > 0) {
          // Wait a bit and retry
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          return attemptGetToken(retriesLeft - 1);
        }
        console.warn(
          "No user found after retries when attempting to refresh token"
        );
        return null;
      }

      // Get token with force refresh parameter
      const token = await user.getIdToken(forceRefresh);
      window.localStorage.setItem(localStorageKeys.TOKEN_KEY, token);
      return token;
    } catch (error) {
      if (retriesLeft > 0) {
        console.debug(
          `Token retrieval attempt failed, ${retriesLeft} retries left`,
          error
        );
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        return attemptGetToken(retriesLeft - 1);
      }

      console.error("Error refreshing Firebase token:", error);
      return null;
    }
  };

  return attemptGetToken(maxRetries);
};

// Check if any of our errors might be auth-related
const isAuthError = (error: any): boolean => {
  // If error message has token expired, it's definitely auth
  if (error?.message?.includes("token expired")) return true;
  if (error?.message?.includes("JWT")) return true;

  // Check the error structure specific to your case
  if (error?.extensions?.code === "validation-failed") {
    // Check if it's related to permissions/auth
    const errorPath = error?.extensions?.path || "";
    const errorMessage = error?.message || "";

    // Add specific checks for your case
    if (errorMessage.includes("field") && errorMessage.includes("not found")) {
      return true;
    }

    return errorPath.includes("selectionSet");
  }

  // Other auth error checks
  if (error.message?.includes("JWT")) return true;
  if (error.message?.includes("token")) return true;
  if (error.message?.includes("permission")) return true;
  if (error.extensions?.code === "UNAUTHENTICATED") return true;

  return false;
};

/**
 * Retries a failed operation with updated authorization headers
 * @param operation - The GraphQL operation that failed
 * @param token - The new authentication token
 * @returns The operation context with updated headers
 */
const updateOperationAuth = (operation: Operation, token: string) => {
  const context = operation.getContext();
  const oldHeaders = context.headers || {};

  // Update operation context with new authorization header
  operation.setContext({
    headers: {
      ...oldHeaders,
      authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Error handling link for Apollo Client that handles token refresh
 * This link intercepts GraphQL errors and attempts to refresh the token if
 * authentication errors are detected
 */

const errorLink = onError(
  ({
    graphQLErrors, // GraphQL specific errors
    networkError, // Network errors
    operation, // The GraphQL operation that failed
    forward, // A function to resend the operation
  }) => {
    // First, check if we have any errors that need handling
    if (!graphQLErrors && !networkError) return;

    // Log all errors for debugging
    if (graphQLErrors) {
      graphQLErrors.forEach((error) => {
        console.debug("GraphQL error:", {
          message: error.message,
          path: error.path,
          extensions: error.extensions,
        });
      });
    }

    if (networkError) {
      console.debug("Network error:", networkError);
    }

    console.log("GQL operation: ", operation.operationName);

    const shouldHandleAuth =
      (networkError && isAuthError(networkError)) ||
      (graphQLErrors && graphQLErrors.some(isAuthError));

    // Let other errors propagate normally
    if (!shouldHandleAuth) return;

    // Add some logging to help debug auth issues
    console.warn("Auth error detected, attempting to refresh token...");

    // Return a new Observable that will handle the token refresh
    return new Observable<FetchResult>((outerObserver) => {
      let isCompleted = false; // Don't proceed if cancelled

      // Start the token refresh process to ensure we have all claims
      getFreshToken({ forceRefresh: true })
        .then((newToken) => {
          if (isCompleted) return;

          if (!newToken) {
            console.warn(
              "Unable to get a new token, clearing token from localStorage"
            );
            window.localStorage.removeItem(localStorageKeys.TOKEN_KEY);

            // Graceful completion instead of error propagation
            // Redirect to login
            window.dispatchEvent(
              new CustomEvent("authNavigation", {
                detail: { route: "/login" },
              })
            );
            outerObserver.complete();
            return;
          }

          console.log("Token refreshed successfully, retrying operation");

          // Update the operation with the new token
          updateOperationAuth(operation, newToken);

          // Retry the operation with new token
          // The forward(operation) function returns another Observable. We need to “connect” these two Observables. That is:
          // The Observable we created to handle the token refresh: outerObserver.
          // The Observable that returns forward(operation), the original operation retry

          forward(operation).subscribe({
            next: (value) => {
              if (!isCompleted) {
                outerObserver.next(value);
              }
            },
            error: (error) => {
              if (!isCompleted) {
                // If we still get an error after refresh, complete the operation
                console.error("Operation failed after token refresh:", error);

                // If it's still an auth error after refreshing token, we have a bigger problem
                if (isAuthError(error)) {
                  console.warn(
                    "Still getting auth errors after token refresh, redirecting to login"
                  );
                  window.dispatchEvent(
                    new CustomEvent("authNavigation", {
                      detail: { route: "/login" },
                    })
                  );
                }

                outerObserver.complete();
              }
            },
            complete: () => outerObserver.complete(),
          });
        })
        .catch((error) => {
          if (!isCompleted) {
            console.error("Token refresh failed:", error);
            outerObserver.complete(); // Complete instead of error
          }
        });

      // Return cleanup function. This function is called when the Observable is unsubscribed
      // Add any cleanup logic here (e.g., abort controller for fetch calls)
      return () => {
        isCompleted = true;
        console.log("Token refresh operation cancelled");
      };
    });
  }
);

// Auth link that checks token before each request
const authLink = setContext(async (_, { headers }) => {
  const token = window.localStorage.getItem(localStorageKeys.TOKEN_KEY);

  // If no token exists get a fresh one
  if (!token) {
    const newToken = await getFreshToken();
    return {
      headers: {
        ...headers,
        authorization: newToken ? `Bearer ${newToken}` : null,
      },
    };
  }

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : null,
    },
  };
});

let apolloClientInstance: ApolloClient<NormalizedCacheObject> | undefined;

export const getApolloClient = (refresh: boolean = false, token?: string) => {
  if (!refresh && apolloClientInstance) {
    return apolloClientInstance;
  }

  // Anonymous Client
  const httpLink = createHttpLink({
    uri: GQL_ENDPOINT,
    // uri: "http://localhost:8080/v1/graphql",
    fetchOptions: {
      referrerPolicy: "unsafe-url",
      credentials: "include", // Include cookies for cross-origin requests
    },
  });

  const link = errorLink.concat(authLink.concat(httpLink));

  apolloClientInstance = new ApolloClient({
    link: link,
    cache: new InMemoryCache(),
    connectToDevTools: process.env.NODE_ENV !== "production", // Enable Apollo dev tools in non-production
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "network-only", // Start with network to ensure fresh data
        nextFetchPolicy(
          currentFetchPolicy,
          {
            // Either "after-fetch" or "variables-changed", indicating why the nextFetchPolicy function was invoked.
            reason,
            // The rest of the options (currentFetchPolicy === options.fetchPolicy).
            options,
            // The original value of options.fetchPolicy, before nextFetchPolicy was applied for the first time.
            initialFetchPolicy,
            // The ObservableQuery associated with this client.watchQuery call.
            observable,
          }
        ) {
          // When variables change, the default behavior is to reset options.fetchPolicy to context.initialFetchPolicy.
          // If you omit this logic, your nextFetchPolicy function can override this default behavior to prevent options.fetchPolicy from changing in this case.
          if (reason === "variables-changed") {
            return initialFetchPolicy;
          }

          if (currentFetchPolicy === "network-only") {
            return "network-only";
          }

          // Leave all other fetch policies unchanged.
          return currentFetchPolicy;
        },
        errorPolicy: "all", // Don't treat GraphQL errors as fatal
      },
      query: {
        fetchPolicy: "network-only",
        errorPolicy: "all", // Don't treat GraphQL errors as fatal
      },
      mutate: {
        fetchPolicy: "no-cache",
        errorPolicy: "all",
      },
    },
  });

  return apolloClientInstance;
};
